/**
 * In-memory реестр нативных `File` и флага `changed` по `__clientRowKey`.
 * Store (UniDataTable) — SSOT для списка строк; реестр — для несериализуемых полей UI/upload.
 *
 * @see dev/contracts/attachments.md — «Состояние ленты: store vs uploadedFiles», фаза E
 */
import type { FileItem } from '@/common/types/fileItem'
import {
  alignAttachmentClientRowKey,
  attachmentItemsReferSameRow,
  ensureAttachmentClientRowKey,
  getAttachmentClientRowKey,
  getScalarFromFields,
  isAttachmentItemChanged,
  rowHasServerCode,
  withAttachmentItemChanged,
} from '@/common/utils/fileAttachmentsUniData'

export type AttachmentNativeFileEntry = {
  file: File
  changed?: boolean
}

/** Совпадение native `File` со строкой ленты по имени и размеру (fallback, если `__clientRowKey` разошёлся). */
export function nativeFileMatchesRowNameAndSize(
  file: File,
  fields: Record<string, unknown>,
): boolean {
  const name = String(getScalarFromFields(fields, 'name', 'fileName') ?? '').trim()
  const size = getScalarFromFields(fields, 'size', '__size')
  if (!name || size == null) return false
  return file.name === name && file.size === Number(size)
}

export function createAttachmentNativeFileRegistry() {
  const byClientKey = new Map<string, AttachmentNativeFileEntry>()

  function getEntry(clientKey: string): AttachmentNativeFileEntry | undefined {
    return byClientKey.get(clientKey)
  }

  function registerItem(item: FileItem): string | null {
    const fields = (item.fields ?? {}) as Record<string, unknown>
    if (rowHasServerCode(fields)) {
      const key = getAttachmentClientRowKey(fields)
      if (key) byClientKey.delete(key)
      return null
    }
    if (!(item.file instanceof File)) return null
    const clientKey = ensureAttachmentClientRowKey(item)
    byClientKey.set(clientKey, {
      file: item.file,
      changed: item.changed ?? isAttachmentItemChanged(item),
    })
    return clientKey
  }

  /** Синхронизировать реестр с актуальным списком строк (add/update/prune). */
  function syncFromItems(items: FileItem[]): void {
    const alive = new Set<string>()
    for (const item of items) {
      const fields = (item.fields ?? {}) as Record<string, unknown>
      const existingKey = getAttachmentClientRowKey(fields)
      if (rowHasServerCode(fields)) {
        if (existingKey) byClientKey.delete(existingKey)
        continue
      }
      const key = registerItem(item)
      if (key) {
        alive.add(key)
        continue
      }
      // Persist без File на объекте (prepare Р7) — не вычищать native File.
      if (existingKey && byClientKey.has(existingKey)) {
        alive.add(existingKey)
        continue
      }
      for (const [keepKey, entry] of byClientKey) {
        if (nativeFileMatchesRowNameAndSize(entry.file, fields)) {
          if (existingKey && existingKey !== keepKey) {
            byClientKey.set(existingKey, entry)
            byClientKey.delete(keepKey)
            alive.add(existingKey)
          } else {
            alive.add(keepKey)
          }
          break
        }
      }
    }
    for (const key of [...byClientKey.keys()]) {
      if (!alive.has(key)) byClientKey.delete(key)
    }
  }

  function clearForItem(item: FileItem): void {
    const key = getAttachmentClientRowKey((item.fields ?? {}) as Record<string, unknown>)
    if (key) byClientKey.delete(key)
  }

  function findEntryByNameAndSize(fields: Record<string, unknown>): AttachmentNativeFileEntry | undefined {
    for (const entry of byClientKey.values()) {
      if (nativeFileMatchesRowNameAndSize(entry.file, fields)) return entry
    }
    return undefined
  }

  function findEntryForRow(fields: Record<string, unknown>): AttachmentNativeFileEntry | undefined {
    const clientKey = getAttachmentClientRowKey(fields)
    if (clientKey) {
      const byKey = byClientKey.get(clientKey)
      if (byKey) return byKey
    }
    return findEntryByNameAndSize(fields)
  }

  function hydrateItem(row: FileItem): FileItem {
    if (row.file instanceof File) return row
    const fields = (row.fields ?? {}) as Record<string, unknown>
    if (rowHasServerCode(fields)) return row
    const entry = findEntryForRow(fields)
    if (!entry) return row
    return applyNativeFileEntry(row, entry)
  }

  function hydrateItems(rows: FileItem[]): FileItem[] {
    return rows.map(hydrateItem)
  }

  function clearAll(): void {
    byClientKey.clear()
  }

  return {
    getEntry,
    findEntryByNameAndSize,
    registerItem,
    syncFromItems,
    clearForItem,
    hydrateItem,
    hydrateItems,
    clearAll,
  }
}

export type AttachmentNativeFileRegistry = ReturnType<typeof createAttachmentNativeFileRegistry>

const storesByElementId = new Map<number, AttachmentNativeFileRegistry>()

function applyNativeFileEntry(row: FileItem, entry: AttachmentNativeFileEntry): FileItem {
  const hydrated: FileItem = { ...row, file: entry.file, changed: entry.changed ?? row.changed }
  if (entry.changed === true || isAttachmentItemChanged(hydrated)) {
    return withAttachmentItemChanged(hydrated)
  }
  return hydrated
}

/** XML / formPath id реестра native File: только `> 0` (path `-1`/`0` — не ключ). */
export function resolvePositiveElementId(
  formPathElementId?: unknown,
  xmlElementId?: unknown,
): number | null {
  const path = Number(formPathElementId)
  if (Number.isFinite(path) && path > 0) return path
  const xml = Number(xmlElementId)
  if (Number.isFinite(xml) && xml > 0) return xml
  return null
}

export function getAttachmentNativeFileStore(elementId: number): AttachmentNativeFileRegistry {
  const eid = Number(elementId)
  if (!Number.isFinite(eid) || eid <= 0) {
    throw new Error('getAttachmentNativeFileStore: invalid elementId')
  }
  let store = storesByElementId.get(eid)
  if (!store) {
    store = createAttachmentNativeFileRegistry()
    storesByElementId.set(eid, store)
  }
  return store
}

/** Unmount FILEATTACHMENTS — локальные `File` не переживают инстанс элемента. */
export function removeAttachmentNativeFileStore(elementId: number): void {
  const eid = Number(elementId)
  if (!Number.isFinite(eid) || eid <= 0) return
  const store = storesByElementId.get(eid)
  if (!store) return
  store.clearAll()
  storesByElementId.delete(eid)
}

export function clearAllAttachmentNativeFileStores(): void {
  for (const store of storesByElementId.values()) {
    store.clearAll()
  }
  storesByElementId.clear()
}

/** Есть native `File` в реестре элемента или в любом store модуля (clientKey / name+size). */
export function hasNativeFileForAttachmentRow(
  row: FileItem,
  elementId?: number | null,
): boolean {
  if (row.file instanceof File) return true
  const fields = (row.fields ?? {}) as Record<string, unknown>
  if (rowHasServerCode(fields)) return false
  const clientKey = getAttachmentClientRowKey(fields)
  const tryStore = (store: AttachmentNativeFileRegistry): boolean => {
    if (clientKey && store.getEntry(clientKey)) return true
    return Boolean(store.findEntryByNameAndSize(fields))
  }
  const eid = Number(elementId)
  if (Number.isFinite(eid) && eid > 0) {
    try {
      if (tryStore(getAttachmentNativeFileStore(eid))) return true
    } catch {
      // ignore invalid elementId
    }
  }
  for (const [id, store] of storesByElementId) {
    if (id === eid) continue
    if (tryStore(store)) return true
  }
  return false
}

function hydrateItemFromAnyNativeStore(row: FileItem, skipElementId?: number): FileItem {
  if (row.file instanceof File) return row
  const fields = (row.fields ?? {}) as Record<string, unknown>
  if (rowHasServerCode(fields)) return row
  const clientKey = getAttachmentClientRowKey(fields)
  for (const [id, store] of storesByElementId) {
    if (id === skipElementId) continue
    if (clientKey) {
      const entry = store.getEntry(clientKey)
      if (entry) return applyNativeFileEntry(row, entry)
    }
    const byName = store.findEntryByNameAndSize(fields)
    if (byName) return applyNativeFileEntry(row, byName)
  }
  return row
}

/** Store rows → `FileItem[]` с восстановленным `file` / `changed` из реестра элемента. */
export function hydrateAttachmentFileItemsFromStore(
  elementId: number | null | undefined,
  rows: FileItem[],
): FileItem[] {
  const eid = Number(elementId)
  const primary =
    Number.isFinite(eid) && eid > 0
      ? getAttachmentNativeFileStore(eid).hydrateItems(rows)
      : rows
  return primary.map((row) => hydrateItemFromAnyNativeStore(row, eid))
}

function attachmentRowsShareNameAndSize(a: FileItem, b: FileItem): boolean {
  const af = (a.fields ?? {}) as Record<string, unknown>
  const bf = (b.fields ?? {}) as Record<string, unknown>
  const aName = String(getScalarFromFields(af, 'name', 'fileName') ?? '').trim()
  const bName = String(getScalarFromFields(bf, 'name', 'fileName') ?? '').trim()
  const aSize = getScalarFromFields(af, 'size', '__size')
  const bSize = getScalarFromFields(bf, 'size', '__size')
  return Boolean(aName && aName === bName && aSize != null && aSize === bSize)
}

function copyStoreClientRowKeys(source: FileItem[], storeRows: FileItem[]): FileItem[] {
  if (!Array.isArray(storeRows) || storeRows.length === 0) return source
  return source.map((row) => {
    if (getAttachmentClientRowKey((row.fields ?? {}) as Record<string, unknown>)) return row
    const match =
      storeRows.find((storeRow) => attachmentItemsReferSameRow(row, storeRow)) ??
      storeRows.find((storeRow) => attachmentRowsShareNameAndSize(row, storeRow))
    if (!match) return row
    return alignAttachmentClientRowKey(match, row)
  })
}

/**
 * Строки для getFullData upload: store или component, всегда с native File из реестра.
 * Component rows без `File` (после persist prepare) не должны обходить гидрацию.
 */
export function resolveAttachmentItemsForUpload(
  elementId: number | null | undefined,
  storeRows: FileItem[],
  componentRows?: FileItem[] | null,
): FileItem[] {
  const source =
    Array.isArray(componentRows) && componentRows.length > 0 ? componentRows : storeRows
  const aligned = copyStoreClientRowKeys(source, storeRows)
  return hydrateAttachmentFileItemsFromStore(elementId, aligned)
}
